import { useAxios } from 'hooks/useAxios';
import { AxiosErrorHandler } from 'shared/Helpers';
import { BASE_URL_V2 } from 'shared/constants';

export interface ClientSearchResult {
  id: number;
  name: string;
  salesforce_id: string | null;
  domain: string | null;
}

export interface ClientPortfolio {
  id: number;
  name: string;
  salesforceId: string;
  domain: string;
  children: ClientPortfolio[];
}

export interface ClientSupplierVendor {
  id: number;
  name: string;
  domain: string | null;
}

export interface ClientSupplier {
  id: number;
  supplierId: string;
  supplierName: string;
  supplierDomain: string | null;
  vendor: ClientSupplierVendor | null;
}

export interface UpsertClientSupplierBody {
  vendor_id?: number | null;
  supplier_id: string;
  supplier_name: string;
  supplier_domain?: string | null;
}

export interface UpsertClientSupplierResponse {
  id: number;
  client_id: number;
  vendor_id: number | null;
  supplier_id: string;
  supplier_name: string;
  supplier_domain: string | null;
}

export interface CreateAndMapVendorBody {
  vendorName: string;
  vendorDomain?: string | null;
  dedupAcknowledged: boolean;
  supplier_id: string;
  supplier_name: string;
  supplier_domain?: string | null;
}

export interface CreateAndMapVendorResponse {
  vendorId: number;
  vendorName: string;
  vendorDomain: string | null;
  vendorSimKey: string;
  clientSupplierId: number;
}

/**
 * Error response body returned when the create-and-map endpoint detects
 * near-duplicate vendors and dedupAcknowledged is false (409 Conflict).
 */
export interface DedupConflictResponse {
  statusCode: number;
  error: string;
  message: string;
  candidates: Array<{
    id: number;
    name: string;
    domain: string | null;
    simKey: string;
  }>;
}

export interface BulkMatchCandidate {
  id: number;
  name: string;
  domain: string | null;
  simKey: string;
  confidence: 'exact' | 'high';
  distance: number;
}

export interface BulkMatchNeedsReview {
  supplierId: number;
  supplierExternalId: string;
  supplierName: string;
  candidates: BulkMatchCandidate[];
}

export interface BulkMatchNoMatch {
  supplierId: number;
  supplierExternalId: string;
  supplierName: string;
}

export interface BulkMatchResponse {
  autoMappedCount: number;
  needsReview: BulkMatchNeedsReview[];
  noMatch: BulkMatchNoMatch[];
}

/**
 * Custom hook that provides API functions for managing client data.
 * @returns {object} An object containing all client management API functions
 */
export const useClientApi = () => {
  const { axios } = useAxios(BASE_URL_V2);

  /**
   * Retrieves the client portfolio with hierarchical structure.
   * @param {number} [profileId] - Optional profile ID to filter the portfolio
   * @returns {Promise<ClientPortfolio[] | undefined>} Promise that resolves to the client portfolio data, or undefined if an error occurs
   */
  const getClientPortfolio = async (profileId?: number): Promise<ClientPortfolio[] | undefined> => {
    try {
      const response = await axios.get('/clients/portfolio', {
        params: profileId ? { profileId } : undefined
      });
      if (response?.status === 200) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to fetch client portfolio'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Retrieves client ID from Salesforce ID.
   * @param {string} salesforce_id - The Salesforce ID to look up
   * @returns {Promise<{ client_id: number | null } | undefined>} Promise that resolves to an object containing the client_id, or undefined if an error occurs
   */
  const getClientIdFromSalesforceId = async (
    salesforce_id: string
  ): Promise<{ client_id: number | null } | undefined> => {
    try {
      const response = await axios.get('/clients/id', {
        params: { salesforce_id }
      });
      if (response?.status === 200) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to fetch client ID'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Searches clients by name (partial, case-insensitive match).
   * @param {string} name - The search term
   * @returns {Promise<ClientSearchResult[] | undefined>} Promise that resolves to matching clients, or undefined if an error occurs
   */
  const searchClientsByName = async (name: string): Promise<ClientSearchResult[] | undefined> => {
    try {
      const response = await axios.get('/clients/search-by-name', { params: { name } });
      if (response?.status === 200) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to search clients'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Retrieves supplier mappings for a given client, including vendor id, name, and domain.
   * @param {number} clientId - The client ID to fetch suppliers for
   * @returns {Promise<ClientSupplier[] | undefined>} Promise that resolves to the client's supplier records, or undefined if an error occurs
   */
  const getClientSuppliers = async (clientId: number): Promise<ClientSupplier[] | undefined> => {
    try {
      const response = await axios.get(`/clients/${clientId}/suppliers`);
      if (response?.status === 200) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to fetch client suppliers'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Creates or updates a supplier mapping for a given client.
   * Upserts on (client_id, supplier_id) — creates if not found, updates if exists.
   * @param {number} clientId - The client ID
   * @param {UpsertClientSupplierBody} body - vendor_id, supplier_id, supplier_name, supplier_domain
   * @returns {Promise<UpsertClientSupplierResponse | undefined>} Promise that resolves to the created or updated record, or undefined if an error occurs
   */
  const upsertClientSupplier = async (
    clientId: number,
    body: UpsertClientSupplierBody
  ): Promise<UpsertClientSupplierResponse | undefined> => {
    try {
      const response = await axios.post(`/clients/${clientId}/supplier`, body);
      if (response?.status === 201) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to upsert client supplier'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Deletes a client supplier mapping by record ID, scoped to the given client.
   * @param {number} clientId - The client ID
   * @param {number} id - The client_supplier record ID
   * @returns {Promise<UpsertClientSupplierResponse | undefined>} The deleted record, or undefined on error
   */
  const deleteClientSupplier = async (
    clientId: number,
    id: number
  ): Promise<UpsertClientSupplierResponse | undefined> => {
    try {
      const response = await axios.delete(`/clients/${clientId}/supplier/${id}`);
      if (response?.status === 200) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to delete client supplier'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Creates a new vendor and maps it to a client supplier in a single transaction.
   * If near-duplicate vendors exist and dedupAcknowledged is false, the API returns
   * a 409 response with the candidate list — this function returns the parsed
   * DedupConflictResponse in that case instead of throwing.
   * @param {number} clientId - The client ID
   * @param {CreateAndMapVendorBody} body - Vendor + supplier details with dedup flag
   * @returns {Promise<CreateAndMapVendorResponse | DedupConflictResponse | undefined>}
   */
  const createAndMapVendor = async (
    clientId: number,
    body: CreateAndMapVendorBody
  ): Promise<CreateAndMapVendorResponse | DedupConflictResponse | undefined> => {
    try {
      const response = await axios.post(`/clients/${clientId}/supplier/create-and-map`, body);
      if (response?.status === 201) {
        return response?.data as CreateAndMapVendorResponse;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to create and map vendor'));
    } catch (e: any) {
      // 409 Conflict means dedup candidates exist — return the structured response
      if (e?.response?.status === 409 && e?.response?.data?.candidates) {
        return e.response.data as DedupConflictResponse;
      }
      AxiosErrorHandler(e);
    }
  };

  /**
   * Bulk-matches all unmatched suppliers for a given client.
   * Auto-maps single high-confidence matches and returns ambiguous/unresolved records.
   * @param {number} clientId - The client ID
   * @returns {Promise<BulkMatchResponse | undefined>} Categorized results, or undefined on error
   */
  const bulkMatchSuppliers = async (clientId: number): Promise<BulkMatchResponse | undefined> => {
    try {
      const response = await axios.post(`/clients/${clientId}/suppliers/bulk-match`);
      if (response?.status >= 200 && response?.status < 300) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to bulk match suppliers'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  return {
    getClientPortfolio,
    getClientIdFromSalesforceId,
    searchClientsByName,
    getClientSuppliers,
    upsertClientSupplier,
    deleteClientSupplier,
    createAndMapVendor,
    bulkMatchSuppliers
  };
};
